import { useEffect, useState } from "react";
import { fetchChildScores } from "@/lib/api";
import {
  GraduationCap, Scale, Activity, Heart, TrendingUp, TrendingDown, Minus, Loader2,
} from "lucide-react";

const META = {
  academic: { Icon: GraduationCap, label: "Academic Score" },
  activity_balance: { Icon: Scale, label: "Activity Balance" },
  engagement: { Icon: Activity, label: "Engagement" },
  family_calm: { Icon: Heart, label: "Family Calm" },
};

const CALM_BAND_LABEL = {
  thriving: "Thriving",
  stable: "Stable",
  busy: "Busy",
  overloaded: "Overloaded",
};

const BAND_COLOR = {
  improving: { bg: "bg-emerald-50", text: "text-emerald-700", bar: "bg-emerald-500", border: "border-emerald-200", label: "Improving" },
  stable:    { bg: "bg-amber-50",   text: "text-amber-700",   bar: "bg-amber-500",   border: "border-amber-200",   label: "Stable" },
  needs_attention: { bg: "bg-rose-50", text: "text-rose-700", bar: "bg-rose-500",    border: "border-rose-200",    label: "Needs attention" },
};

export default function ChildGrowthScores({ childId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchChildScores(childId)
      .then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [childId]);

  if (loading) {
    return (
      <div className="fcc-card p-8 text-center text-[var(--fcc-muted)]" data-testid="child-growth-loading">
        <Loader2 className="w-5 h-5 mx-auto animate-spin" />
        <p className="text-sm mt-3">Computing growth scores…</p>
      </div>
    );
  }
  if (!data) return null;

  const blocks = [
    { key: "academic", ...data.academic },
    { key: "activity_balance", ...data.activity_balance },
    { key: "engagement", ...data.engagement },
    { key: "family_calm", ...(data.family_calm || data.family_stress) },
  ];

  return (
    <section data-testid="child-growth-scores" className="fcc-card p-6 sm:p-7">
      <header className="flex items-baseline justify-between mb-5">
        <div>
          <p className="label-overline">Child Growth Dashboard</p>
          <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1">How {data.child_name} is doing</h2>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">Updated {new Date(data.generated_at).toLocaleDateString()}</span>
      </header>
      <ul className="grid sm:grid-cols-2 gap-4">
        {blocks.map((b) => <ScoreTile key={b.key} block={b} />)}
      </ul>
    </section>
  );
}

function ScoreTile({ block }) {
  const meta = META[block.key];
  const Icon = meta.Icon;
  const colors = BAND_COLOR[block.band] || BAND_COLOR.stable;
  const Trend = block.trend === "up" ? TrendingUp : block.trend === "down" ? TrendingDown : Minus;
  const trendCls = block.trend === "up" ? "text-emerald-600" : block.trend === "down" ? "text-rose-600" : "text-[var(--fcc-muted)]";
  const bandLabel = block.key === "family_calm" && block.calm_band
    ? CALM_BAND_LABEL[block.calm_band] || colors.label
    : colors.label;
  return (
    <li data-testid={`growth-score-${block.key}`} className={`rounded-2xl border ${colors.border} ${colors.bg} p-5`}>
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2">
          <Icon className={`w-4 h-4 ${colors.text}`} />
          <p className="font-semibold text-sm text-[var(--fcc-ink)]">{meta.label}</p>
        </span>
        <span className={`inline-flex items-center gap-1 text-xs font-semibold ${colors.text}`}>
          <Trend className={`w-3.5 h-3.5 ${trendCls}`} />
          {bandLabel}
        </span>
      </div>
      <div className="flex items-baseline gap-2 mt-3">
        <span className="font-display text-4xl text-[var(--fcc-ink)] leading-none">{block.score}</span>
        <span className="text-xs text-[var(--fcc-muted)]">/ 100</span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-white/60 overflow-hidden" aria-label={`${meta.label} progress`}>
        <div className={`h-full ${colors.bar} transition-all`} style={{ width: `${Math.max(2, Math.min(100, block.score))}%` }} />
      </div>
      <p className="text-xs text-[var(--fcc-ink-soft)] mt-3 leading-relaxed">{block.explanation}</p>
      {block.buckets && (
        <p className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] mt-2">
          Sports {block.buckets.sports} · Activities {block.buckets.activities} · Medical {block.buckets.medical}
        </p>
      )}
    </li>
  );
}
